import { authenticatedFetch, unauthenticatedFetch, ApiError } from './base-client';

/**
 * REST Client for authenticated API calls
 *
 * This client automatically handles authentication by including the user's session token.
 * Use this client for all API endpoints that require authentication.
 *
 * For unauthenticated API calls, use the unauthenticated functions below.
 */

export async function get<T>(endpoint: string): Promise<T> {
  try {
    const response = await authenticatedFetch(endpoint);
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('GET request failed');
  }
}

export async function post<T>(endpoint: string, body?: unknown): Promise<T> {
  try {
    const response = await authenticatedFetch(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('POST request failed');
  }
}

export async function put<T>(endpoint: string, body?: unknown): Promise<T> {
  try {
    const response = await authenticatedFetch(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('PUT request failed');
  }
}

export async function patch<T>(endpoint: string, body?: unknown): Promise<T> {
  try {
    const response = await authenticatedFetch(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('PATCH request failed');
  }
}

export async function del<T>(endpoint: string): Promise<T> {
  try {
    const response = await authenticatedFetch(endpoint, {
      method: 'DELETE',
    });
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('DELETE request failed');
  }
}

/**
 * Unauthenticated REST Client functions
 *
 * These functions do not include authentication headers.
 * Use these for public API endpoints that don't require authentication.
 */

export async function getUnauthenticated<T>(endpoint: string): Promise<T> {
  try {
    const response = await unauthenticatedFetch(endpoint);
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('Unauthenticated GET request failed');
  }
}

export async function postUnauthenticated<T>(endpoint: string, body?: unknown): Promise<T> {
  try {
    const response = await unauthenticatedFetch(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  } catch (error) {
    throw error instanceof ApiError ? error : new ApiError('Unauthenticated POST request failed');
  }
}
